import {
  difference,
  flattenDeep,
  pick,
  mapValues,
  has,
  get,
  omit,
  cloneDeep,
  set,
  reject,
} from 'lodash';
import { v1 as uuid } from 'uuid';
import questionnaireMessages from 'containers/QuestionnairePage/messages';
import abTestQuestions, { PREDEFINED_DEFAULT } from 'data/abTestQuestions';
import vanWestendorpQuestions, {
  PREDEFINED_VAN_WESTENDORP,
} from 'data/vanWestendorpQuestions';
import * as TYPES from 'data/nodeTypes';

import {
  NODE_FEATURE_SUPPORT,
  CONVERSION_SUPPORT,
  QUESTION_WITH_MEDIA_ANSWER,
  FEATURE_PROPERTIES,
  COMMON_QUESTION_PROPERTIES,
  DEFAULT_PROPERTIES,
  PERSISTED_QUESTION_PROPERTIES,
  NOT_PERSISTED_QUESTION_PROPERTIES_BY_TYPE,
  CARRY_FORWARD_FROM,
  NODES_SUPPORT_ANSWERS_WITH_MENTIONS,
  LOOP_AND_MERGE_FROM,
} from 'data/featuresPerQuestion';
import { NEW_QUESTION_DEFAULT_VALUES } from 'data/defaultNodeValues';
import { getEmptyAnswer } from 'containers/QuestionnairePage/GenericQuestion/BipolarQuestion/utils';
import {
  getDefaultConjointProperties,
  getDefaultMaxDiffProperties,
} from 'containers/QuestionnairePage/QuestionGroup/utils';
import {
  isGroup,
  isConjointGroup,
  isMaxDiffGroup,
  isConversationalType,
  isAbGroup,
  isConjointOrMaxDiffGroup,
  isVanWestendorpGroup,
  isSingleQuestionType,
  isNpsQuestionType,
  isMultipleOrMatrixMultipleQuestionType,
} from 'utils/nodeTypes';
import { hasPiping } from 'components/FormattedInput/helpers';

export const questionSupportsFeature = (questionType, feature) =>
  get(NODE_FEATURE_SUPPORT, questionType, []).includes(feature);

export const canConvertQuestion = (sourceQuestionType, targetQuestionType) =>
  get(CONVERSION_SUPPORT, sourceQuestionType, []).includes(targetQuestionType);

const getDefaultCommonProperties = (type) => ({
  prompt: isNpsQuestionType(type)
    ? questionnaireMessages.npsPlaceholder.defaultMessage
    : '',
  htmlPrompt: isNpsQuestionType(type)
    ? `<div>${questionnaireMessages.npsPlaceholder.defaultMessage}</div>`
    : '',
  isScreening: false,
  type,
});

export const getAllowedCarryForwardQuestionTypes = (targetType) =>
  get(CARRY_FORWARD_FROM, targetType, []);

export const canCarryForwardFrom = ({ targetType, sourceType }) =>
  getAllowedCarryForwardQuestionTypes(targetType).includes(sourceType);

export const canLoopAndMergeFrom = (sourceType) =>
  LOOP_AND_MERGE_FROM.includes(sourceType);

export const questionAnswerSupportsMedia = (questionType) =>
  QUESTION_WITH_MEDIA_ANSWER.includes(questionType);

export const getBackendSupportedQuestionProperties = (questionType) => {
  const features = NODE_FEATURE_SUPPORT[questionType];
  const properties = features?.map((feature) => FEATURE_PROPERTIES[feature]);
  return flattenDeep([COMMON_QUESTION_PROPERTIES, properties]);
};

export const generateQuestion = (type = TYPES.QUESTION_SINGLE, refId) => ({
  ...getDefaultQuestionProperties(type, true),
  refId: refId || uuid(),
});

export const getDefaultQuestionProperties = (
  questionType,
  enrichWithDefaultValues,
) => {
  const features = NODE_FEATURE_SUPPORT[questionType];
  const defaults = features?.reduce(
    (props, feature) => ({
      ...props,
      ...DEFAULT_PROPERTIES[feature],
    }),
    getDefaultCommonProperties(questionType),
  );

  const defaultQuestion = mapValues(defaults, (value) => {
    if (typeof value === 'function') {
      if (value === Array) {
        return value();
      }

      return value(questionType);
    }
    return value;
  });

  if (enrichWithDefaultValues && NEW_QUESTION_DEFAULT_VALUES[questionType]) {
    return NEW_QUESTION_DEFAULT_VALUES[questionType](defaultQuestion);
  }

  return defaultQuestion;
};

export const getConversionNodeData = (type, previousNode) => {
  if (isConversationalType(type)) {
    const question = generateQuestion(type, previousNode.refId);
    return question;
  }

  if (!isGroup(type) && !isGroup(previousNode.type)) {
    return getDefaultQuestionProperties(type);
  }

  let newNode = generateQuestion(type, previousNode.refId);

  newNode = {
    ...newNode,
    groupRefId: undefined,
    ...generateGroupData(newNode.refId, type, newNode.attributes),
    pristineValues: defaultPristineValues(true, type),
  };

  if (newNode.questions) {
    newNode.questions = newNode.questions.map((question) => ({
      ...question,
      pristineValues: defaultPristineValues(),
    }));
  }

  if (isGroup(type) && isGroup(previousNode)) {
    const changedFromConjointToMaxDiff =
      isConjointGroup(previousNode.type) && isMaxDiffGroup(type);
    const changedFromMaxDiffToConjoint =
      isMaxDiffGroup(previousNode.type) && isConjointGroup(type);

    if (changedFromConjointToMaxDiff || changedFromMaxDiffToConjoint) {
      newNode.pristineValues = previousNode.pristineValues;
      // we shouldn't keep the properties since they are generated from the doe service and we need
      // to reset those upon changes.
      if (isConjointGroup(type)) {
        newNode.properties = getDefaultConjointProperties();
      } else if (isMaxDiffGroup(type)) {
        newNode.properties = getDefaultMaxDiffProperties();
      }

      if (isConjointGroup(type) && previousNode.attributes.length === 1) {
        newNode.attributes = [
          previousNode.attributes[0],
          newNode.attributes[1],
        ];
        newNode.properties = {
          ...newNode.properties,
          doeStructureVersion: newNode.properties.doeStructureVersion + 1,
        };
      } else if (isMaxDiffGroup(type)) {
        newNode.attributes = [previousNode.attributes[0]];
        newNode.properties = {
          ...newNode.properties,
          doeStructureVersion: newNode.properties.doeStructureVersion + 1,
        };
      } else {
        newNode.attributes = previousNode.attributes;
      }

      newNode.questions[0].pristineValues =
        previousNode.questions[0].pristineValues;
      newNode.questions[0].prompt = previousNode.questions[0].prompt;
      newNode.questions[0].htmlPrompt = previousNode.questions[0].htmlPrompt;
    }
  }

  return newNode;
};

export const getPersistedQuestionProperties = (questionType) =>
  difference(
    PERSISTED_QUESTION_PROPERTIES,
    NOT_PERSISTED_QUESTION_PROPERTIES_BY_TYPE[questionType],
  );

export const getCleanQuestionData = (questionData, defaultData = {}) => ({
  ...defaultData,
  ...pick(getQuestionWithCleanAnswers(questionData), [
    ...getBackendSupportedQuestionProperties(questionData.type),
    ...PERSISTED_QUESTION_PROPERTIES,
  ]),
});

export const hasPipingOnAnswerItem = (answer) =>
  hasPiping(answer.body) ||
  hasPiping(answer.afterLabel) ||
  hasPiping(answer.beforeLabel) ||
  hasPiping(answer.label);

export const isNodeSupportAnswersWithMentions = (node) =>
  NODES_SUPPORT_ANSWERS_WITH_MENTIONS.includes(node.type);

export const getQuestionWithCleanAnswers = (questionData) => {
  const answerPaths = questionData.isScreening
    ? ['answers']
    : [
        'answers',
        'scorepoints',
        'slider.ticks',
        'matrix.matrixAnswers',
        'matrix.columns',
        'matrix.matrixExtraLabels',
      ];
  let cleanedQuestionData = getValidAnswersPerQuestionType(questionData);

  answerPaths.forEach((answerPath) => {
    const withImages = removeUnsupportedImagesFromAnswers(
      cleanedQuestionData,
      answerPath,
    );
    cleanedQuestionData = removeUnsupportedPropertiesFromAnswers(
      withImages,
      answerPath,
    );

    // multiple & matrix multiple types are supporting exclusive feature
    if (!isMultipleOrMatrixMultipleQuestionType(questionData.type)) {
      cleanedQuestionData = removeExclusiveDataFromAnswers(
        cleanedQuestionData,
        answerPath,
      );
    }

    if (isNodeSupportAnswersWithMentions(questionData)) {
      cleanedQuestionData = addPipingToQuestionAnswers(
        cleanedQuestionData,
        answerPath,
      );
    }
  });
  return cleanedQuestionData;
};

const removeExclusiveDataFromAnswers = (questionData, answerPath) => {
  const hasAnswers = has(questionData, answerPath);
  if (!hasAnswers) {
    return questionData;
  }
  const answers = get(questionData, answerPath);
  const nonExclusiveAnswers = answers.map((answer) =>
    omit(answer, 'isExclusive'),
  );
  const updatedData = cloneDeep(questionData);
  set(updatedData, answerPath, nonExclusiveAnswers);
  return updatedData;
};

const addPipingToQuestionAnswers = (questionData, answerPath) => {
  const hasAnswers = has(questionData, answerPath);
  if (!hasAnswers) {
    return questionData;
  }
  const answers = get(questionData, answerPath);
  const answersWithPiping = answers.map((answer) => ({
    ...answer,
    hasPiping: hasPipingOnAnswerItem(answer),
  }));
  const updatedData = cloneDeep(questionData);
  set(updatedData, answerPath, answersWithPiping);
  return updatedData;
};

const removeUnsupportedImagesFromAnswers = (questionData, answerPath) => {
  const supportsMedia = questionAnswerSupportsMedia(questionData.type);
  const hasAnswers = has(questionData, answerPath);

  if (!hasAnswers) {
    return questionData;
  }

  if (!supportsMedia && answerPath !== 'matrix.matrixAnswers') {
    const answers = get(questionData, answerPath);
    const answersWithoutImage = (answers || []).map((answer) =>
      omit(answer, ['media']),
    );
    const updatedData = cloneDeep(questionData);
    set(updatedData, answerPath, answersWithoutImage);
    return updatedData;
  }

  // Matrix statement side media lives on matrixAnswers for bipolar.
  // Keep beforeMedia/afterMedia when present; strip plain `media` only when unsupported.
  if (answerPath === 'matrix.matrixAnswers') {
    const answers = get(questionData, answerPath);
    const answersWithSideMedia = (answers || []).map((answer) => {
      if (supportsMedia) {
        return answer;
      }
      return omit(answer, ['media']);
    });
    const updatedData = cloneDeep(questionData);
    set(updatedData, answerPath, answersWithSideMedia);
    return updatedData;
  }

  const answers = get(questionData, answerPath);
  const updatedData = cloneDeep(questionData);
  set(updatedData, answerPath, answers);
  return updatedData;
};

const removeUnsupportedPropertiesFromAnswers = (questionData, answerPath) => {
  const hasAnswers = has(questionData, answerPath);
  if (hasAnswers) {
    const answers = get(questionData, answerPath);
    const answersWithoutUnsupportedProperties = answers.map((answer) =>
      omit(answer, ['isEmptyAnswer', 'isEmptyTick']),
    );
    const updatedData = cloneDeep(questionData);
    set(updatedData, answerPath, answersWithoutUnsupportedProperties);
    return updatedData;
  }
  return questionData;
};

const getValidAnswersPerQuestionType = (questionData) => {
  const questionType = get(questionData, 'type', '');

  switch (questionType) {
    case TYPES.QUESTION_SINGLE:
    case TYPES.QUESTION_MULTIPLE:
    case TYPES.QUESTION_RANKING:
      return rejectAtPath(
        questionData,
        'answers',
        ({ isEmptyAnswer }) => isEmptyAnswer,
      );
    case TYPES.QUESTION_MATRIX_SINGLE:
    case TYPES.QUESTION_MATRIX_MULTIPLE:
    case TYPES.QUESTION_MATRIX_BIPOLAR: {
      const withRemovedAnswers = rejectAtPath(
        questionData,
        'answers',
        ({ isEmptyAnswer }) => isEmptyAnswer,
      );
      return rejectAtPath(
        withRemovedAnswers,
        'scorepoints',
        ({ isEmptyAnswer }) => isEmptyAnswer,
      );
    }
    case TYPES.QUESTION_SLIDER:
      return rejectAtPath(
        questionData,
        'slider.ticks',
        ({ isEmptyTick }) => isEmptyTick,
      );
    default:
      return questionData;
  }
};

const rejectAtPath = (questionData, pathToAnswers, emptyMatcher) => {
  const answers = get(questionData, pathToAnswers, null);
  if (!answers) {
    return questionData;
  }
  // do the cleaning
  const cleanedAnswers = reject(answers, emptyMatcher);
  const updatedQuestionData = cloneDeep(questionData);
  set(updatedQuestionData, pathToAnswers, cleanedAnswers);
  return updatedQuestionData;
};

export const defaultPristineValues = (pristine = true, nodeType) => {
  switch (nodeType) {
    case TYPES.GROUP_VAN_WESTENDORP:
    case TYPES.GROUP_BASIC:
      return {};
    case TYPES.GROUP_AB_TEST:
      return {};
    case TYPES.GROUP_CONJOINT:
      return {
        isPristinePrompt: pristine,
        isPristineAttributes: pristine,
        isPristineAttributeValues: pristine,
      };
    case TYPES.GROUP_MAXDIFF:
      return {
        isPristinePrompt: pristine,
        isPristineAttributes: pristine,
        isPristineAttributeValues: pristine,
      };
    case TYPES.QUESTION_CONVERSATIONAL:
      return {
        isPristineObjective: pristine,
        isPristineGoals: pristine,
      };
    case TYPES.GROUP_LOOP_MERGE: {
      return {
        isPristineVariables: pristine,
      };
    }
    default:
      return {
        isPristinePrompt: pristine,
        isPristineAnswers: pristine,
        isPristineColumns: pristine,
        isPristineRows: pristine,
        isPristineLimits: pristine,
        isPristineRange: pristine,
        isPristineConstantSumRange: pristine,
        isPristineTotalSumDecimals: pristine,
      };
  }
};

export const includesAny = ({ text = '', words = [] }) => {
  const lowerText = text.toLowerCase();
  const lowerTextWords = lowerText.split(' ');
  return words.some((word) => lowerTextWords.includes(word));
};

export const matches = ({ text = '', words = [] }) => {
  const lowerText = text.toLowerCase();
  return words.some((word) => lowerText.trim() === word.trim());
};

const getPredefinedABAnswers = (type, answers) => {
  if (!answers || !isSingleQuestionType(type)) {
    return {};
  }

  return {
    answers: answers.map((body) => ({
      ...getEmptyAnswer(true),
      body,
    })),
  };
};

const getPredefinedVanWestendorpAnswers = (type, answers) => {
  if (!answers) {
    return {};
  }

  return {
    answers: answers.map((body) => ({
      ...getEmptyAnswer(true),
      body,
    })),
  };
};

export const getPredefinedABQuestions = (
  groupRefId,
  key = PREDEFINED_DEFAULT,
  selectedAttributes = [],
) =>
  abTestQuestions[key].map((question) => ({
    ...generateQuestion(question.type),
    groupRefId,
    prompt: question.prompt,
    htmlPrompt: `<div>${question.prompt}</div>`,
    ...getPredefinedABAnswers(question.type, question.answers),
    selectedAttributes,
  }));

export const getPredefinedVanWestendorpQuestions = (
  groupRefId,
  key = PREDEFINED_VAN_WESTENDORP,
  selectedAttributes = [],
) =>
  vanWestendorpQuestions[key].map((question) => ({
    ...generateQuestion(question.type),
    groupRefId,
    prompt: question.prompt,
    htmlPrompt: `<div>${question.prompt}</div>`,
    ...getPredefinedVanWestendorpAnswers(question.type, question.answers),
    selectedAttributes,
  }));

export const getPreSelectedAttributes = (attributes) =>
  attributes.map((attribute) => ({
    refId: attribute.refId,
    selected: true,
  }));

export const generateGroupData = (refId, type, attributes) => {
  if (isAbGroup(type)) {
    return {
      questions: getPredefinedABQuestions(
        refId,
        PREDEFINED_DEFAULT,
        getPreSelectedAttributes(attributes),
      ),
    };
  }

  if (isConjointOrMaxDiffGroup(type)) {
    return {
      questions: getDefaultConjointMaxDiffQuestions(refId, type),
    };
  }

  if (isVanWestendorpGroup(type)) {
    return {
      questions: getPredefinedVanWestendorpQuestions(
        refId,
        PREDEFINED_VAN_WESTENDORP,
        getPreSelectedAttributes(attributes),
      ),
    };
  }

  return {};
};

export const getDefaultConjointMaxDiffQuestions = (groupRefId, groupType) => [
  {
    ...generateQuestion(
      isConjointGroup(groupType)
        ? TYPES.QUESTION_SINGLE
        : TYPES.QUESTION_MAXDIFF,
    ),
    groupRefId,
  },
];
